import type {ReactNode} from 'react';
import React, {useEffect, useId, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLatest} from 'ahooks';
import styles from './drag-drop-overlay.module.scss';
import type {DragDropOverlayProps} from '@Components/drag-drop-overlay/drag-drop-overlay.types';

// TEMPORARY: drag-drop debug instrumentation. Logs every relevant drag event the listener sees
// so we can diagnose the "no overlay, no loader, but image attaches" bug. Set window.DRAG_DEBUG
// to false to silence. Remove this and all `dragDebug(...)` calls once the root cause is found.
const dragDebug = (event: string, data: Record<string, unknown>): void => {
  if ((window as unknown as {DRAG_DEBUG?: boolean}).DRAG_DEBUG === false) {
    return;
  }
  console.log(`[DragDropOverlay] ${event}`, data);
};

const describeTarget = (target: EventTarget | null): string => {
  if (!target) {
    return '(null)';
  }
  if (target === document) {
    return '#document';
  }
  if (!(target instanceof Element)) {
    return '(non-element)';
  }
  const id = target.id ? `#${target.id}` : '';
  const cls = target.className && typeof target.className === 'string' ? `.${target.className.split(/\s+/).slice(0, 2).join('.')}` : '';
  return `${target.tagName.toLowerCase()}${id}${cls}`;
};

export function DragDropOverlay({targetElement, disabled = false, className = '', acceptedTypes = [], ...props}: DragDropOverlayProps): ReactNode {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const dragCounterRef = React.useRef(0);
  const onFilesUploadRef = useLatest(props.onFilesUpload);
  const onInvalidFileTypeErrorRef = useLatest(props.onInvalidFileTypeError);
  const acceptedTypesRef = useLatest(acceptedTypes);
  const instanceId = useId();

  useEffect(() => {
    if (disabled) {
      dragDebug('effect:skipped (disabled)', {instanceId: instanceId});
      return;
    }

    dragDebug('effect:mount listeners attached', {
      instanceId: instanceId,
      hasTargetElement: !!targetElement,
      targetElementCurrent: describeTarget(targetElement?.current ?? null),
    });

    const getTargetElement = (): HTMLElement => {
      return targetElement?.current ?? document.body;
    };

    const isEventWithinTarget = (e: DragEvent): boolean => {
      const target = e.target as Node | null;
      if (!target) {
        return false;
      }

      return getTargetElement().contains(target);
    };

    const handleDragEnter = (e: DragEvent): void => {
      e.preventDefault();
      const within = isEventWithinTarget(e);
      const types = e.dataTransfer ? Array.from(e.dataTransfer.types) : [];
      dragDebug('dragenter', {
        instanceId: instanceId,
        target: describeTarget(e.target),
        relatedTarget: describeTarget(e.relatedTarget),
        within,
        types,
        counterBefore: dragCounterRef.current,
      });

      if (!within) {
        return;
      }

      dragCounterRef.current = dragCounterRef.current + 1;

      if (e.dataTransfer?.types.includes('Files')) {
        setIsVisible(true);
        setHasBeenVisible(true);
        dragDebug('dragenter → setIsVisible(true)', {instanceId: instanceId, counter: dragCounterRef.current});
      } else {
        dragDebug('dragenter incremented counter but types lacks Files', {
          instanceId: instanceId,
          counter: dragCounterRef.current,
          types,
        });
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      const within = isEventWithinTarget(e);
      dragDebug('dragleave', {
        instanceId: instanceId,
        target: describeTarget(e.target),
        relatedTarget: describeTarget(e.relatedTarget),
        within,
        counterBefore: dragCounterRef.current,
      });

      if (!within) {
        return;
      }

      dragCounterRef.current = dragCounterRef.current - 1;

      if (dragCounterRef.current === 0) {
        setIsVisible(false);
        dragDebug('dragleave → setIsVisible(false) (counter hit 0)', {instanceId: instanceId});
      }
    };

    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent): void => {
      e.preventDefault();
      const within = isEventWithinTarget(e);
      const fileCount = e.dataTransfer?.files?.length ?? 0;
      dragDebug('drop', {
        instanceId: instanceId,
        target: describeTarget(e.target),
        within,
        counterBefore: dragCounterRef.current,
        fileCount,
        types: e.dataTransfer ? Array.from(e.dataTransfer.types) : [],
      });

      dragCounterRef.current = 0;
      setIsVisible(false);

      if (!within) {
        return;
      }

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        getOnFileUpload(files);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return (): void => {
      dragDebug('effect:cleanup listeners removed', {instanceId: instanceId});
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [disabled, targetElement]);

  const getOnFileUpload = (files: File[]): void => {
    dragDebug('getOnFileUpload called', {
      instanceId: instanceId,
      fileCount: files.length,
      fileTypes: files.map((f) => f.type),
      acceptedTypes: acceptedTypesRef.current,
    });

    if (files.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    files.forEach((file: File): void => {
      if (acceptedTypesRef.current?.length === 0 || acceptedTypesRef.current.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    dragDebug('getOnFileUpload partitioned', {
      instanceId: instanceId,
      validCount: validFiles.length,
      invalidCount: invalidFiles.length,
    });

    if (invalidFiles.length > 0) {
      onInvalidFileTypeErrorRef.current?.();
    } else if (validFiles.length > 0) {
      dragDebug('calling onFilesUploadRef.current', {instanceId: instanceId, count: validFiles.length});
      onFilesUploadRef.current(validFiles);
    }
  };

  dragDebug('render', {
    instanceId: instanceId,
    isVisible,
    hasBeenVisible,
    counter: dragCounterRef.current,
  });

  const overlayStyle = isVisible ? {display: 'flex'} : undefined;

  const renderHoverContent = (): ReactNode => {
    if (!hasBeenVisible) {
      return null;
    }
    return props.onHoverContent;
  };

  return createPortal(
    <div className={`_full-width _full-height dragdropoverlay ${styles.container} ${className}`} style={overlayStyle}>
      {renderHoverContent()}
    </div>,
    document.body
  );
}
